import React, {
  useCallback,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './animal-event-list.module.css';
import Table from '../../../../components/common/table/table';
import { AnimalEventTable } from '../../../../interfaces/animal-event';
import { LIVESTOCK_ANIMALS_EVENTS } from '../../../../router/routes';
import { ListParams } from '../../../../services/http-client';
import { useService } from '../../../../services/services';
import Toolbar from '../animals/components/toolbar-event-animal';
import { route } from '../../../../services/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import TableVerticalSizeFixer from '../../../../components/common/table/table-vertical-size-fixer/table-vertical-size-fixer';
import { FormMode } from '../../../../enums/form-mode';
import AnimalType from '../../../../enums/animal-type';
import { ButtonComponent } from '../../../../components/form/elements/button';
import OptionModal from '../../../../components/modals/option-modal';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import EditIcon from '../animals/animal-events/EditIcon';
import { Link } from 'react-router-dom';

interface Props {
  useCalendarView: boolean;
  setUseCalendarView: Dispatch<SetStateAction<boolean>>;
  eventsCount: number;
}

export default function AnimalEventList({
  useCalendarView,
  setUseCalendarView,
  eventsCount,
}: Props) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.events.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { animalEventService } = useService();
  const [selectedEvent, setSelectedEvents] = useState([]);
  const [recievedResponse, setRecievedResponse] = useState([]);
  const [updateTable, setUpdateTable] = useState(false);
  const [animalEventIds, setAnimalEventIds] = useState([]);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) => {
      if (updateTable) {
        setUpdateTable(!updateTable);
      }
      return animalEventService.index(
        farmId,
        { page, sort, desc, ...filter },
        true
      );
    },
    [animalEventService, farmId, updateTable, setUpdateTable]
  );

  const viewRoute = useCallback(
    (animalEvent: AnimalEventTable) => {
      if (
        animalEvent.event_type === 'status_report' ||
        animalEvent.event_type.includes('imported_from_ldc')
      ) {
        return;
      }

      return route(LIVESTOCK_ANIMALS_EVENTS + '?to=events', {
        farmId,
        animalType: animalEvent.event_type,
        id: animalEvent.id,
        mode: FormMode.view,
      });
    },
    [farmId]
  );
  const [hasExtendedTable, setHasExtendedTable] = useState<
    boolean | null
  >(null);

  const toolbar = useCallback(
    (params: ListParams): JSX.Element => {
      return (
        <Toolbar
          useCalendarView={useCalendarView}
          setUseCalendarView={setUseCalendarView}
          eventsCount={eventsCount}
          hasAnimalsTable={hasExtendedTable}
        />
      );
    },
    [useCalendarView, setUseCalendarView, eventsCount, hasExtendedTable]
  );

  const useResponse = (responseData) => {
    useEffect(() => {
      setRecievedResponse(responseData);
      if (responseData?.data[0]) {
        setHasExtendedTable('name' in responseData?.data[0]);
      }
    }, [responseData]);
  };
  const setEventDone = () => {
    const animalEventId = selectedEvent.map((item) => item.id);
    const eventId = recievedResponse['data'].filter((item) =>
      animalEventId.includes(item.id)
    );

    if (animalEventId.length === eventId.length) {
      animalEventService
        .setDone(
          farmId,
          eventId.map((event) => event.id)
        )
        .subscribe(() => setUpdateTable(true));
    } else {
      setAnimalEventIds(eventId.map((event) => event.id));
    }
  };

  let columns = hasExtendedTable
    ? [
        {
          accessor: 'has_done',
          header: tp('done'),
          valuable: (value) => {
            if (value) {
              return (
                <FontAwesomeIcon icon={faCheck} className={css['check']} />
              );
            }
          },
        },
        {
          accessor: 'date',
          header: tp('date'),
          className: css['strong'],
          sortable: true,
        },
        {
          accessor: 'animal_id',
          header: tp('animal_id'),
          sortable: true,
          className: css['strong'],
        },
        {
          accessor: 'name',
          header: tp('name'),
          className: css['strong'],
          sortable: true,
        },
        {
          accessor: 'event_type_filter',
          header: tp('event'),
          sortable: true,
          valuable: (value, event) => {
            if (event.event_type.includes(' ')) {
              const types = event.event_type.split(' ');
              if (
                types[0] === AnimalType.grazing ||
                types[0] === AnimalType.weighing
              ) {
                types[0] = types[0] + '_type';
              }
              return (
                <div>
                  {tp(types[0])}{' '}
                  <span className={css['imported_from_ldc']}>
                    ({tp(types[1])})
                  </span>
                </div>
              );
            } else {
              if (
                value === AnimalType.grazing ||
                value === AnimalType.weighing
              ) {
                value = value + '_type';
              }

              return (
                <div className={css['event_type_cell']}>
                  <span>
                    {t(
                      `pages.livestock.animals_and_events.animals.types.${value}`
                    )}
                  </span>
                  <ShowIfAllowed ability={{ title: 'update animal_event' }}>
                    <Link to={viewRoute(event)}>
                      <EditIcon />
                    </Link>
                  </ShowIfAllowed>
                </div>
              );
            }
          },
        },
        {
          accessor: 'information',
          header: tp('information'),
          sortable: true,
        },
      ]
    : [
        {
          accessor: 'has_done',
          header: tp('done'),
          valuable: (value) => {
            if (value) {
              return (
                <FontAwesomeIcon icon={faCheck} className={css['check']} />
              );
            }
          },
        },
        {
          accessor: 'date',
          header: tp('date'),
          className: css['strong'],
          sortable: true,
        },
        {
          accessor: 'event_type',
          header: tp('event'),
          sortable: true,
          valuable: (value) => {
            if (value.includes(' ')) {
              const types = value.split(' ');
              if (
                types[0] === AnimalType.grazing ||
                types[0] === AnimalType.weighing
              ) {
                types[0] = types[0] + '_type';
              }
              return (
                <div>
                  {tp(types[0])}{' '}
                  <span className={css['imported_from_ldc']}>
                    ({tp(types[1])})
                  </span>
                </div>
              );
            } else {
              if (
                value === AnimalType.grazing ||
                value === AnimalType.weighing
              ) {
                value = value + '_type';
              }
              return (
                <div className={css['event_type_cell']}>
                  <span>
                    {t(
                      `pages.livestock.animals_and_events.animals.types.${value}`
                    )}
                  </span>
                </div>
              );
            }
          },
        },
        {
          accessor: 'information',
          header: tp('information'),
          sortable: true,
        },
      ];

  function statusWarningModal() {
    return (
      <OptionModal
        title=""
        content={tp('status_warning_message')}
        onClose={() => setAnimalEventIds([])}
        buttons={[
          { title: t('yes'), name: 'yes', value: true },
          { title: t('no'), name: 'no', value: false },
        ]}
        onChoice={(choice) => {
          if (choice) {
            animalEventService
              .setDone(farmId, animalEventIds)
              .subscribe(() => setUpdateTable(true));
            setAnimalEventIds([]);
          } else {
            setAnimalEventIds([]);
          }
        }}
      />
    );
  }

  return (
    <div className={css['body']}>
      {animalEventIds.length > 0 && statusWarningModal()}
      <TableVerticalSizeFixer>
        <Table<AnimalEventTable>
          request={request}
          keyAccessor="animal_event_id"
          tableAction={
            <ButtonComponent
              disabled={selectedEvent.length === 0}
              name={'event-status-action'}
              click={setEventDone}
              label={tp('mark_as_done')}
            />
          }
          toolbar={toolbar}
          paginate={false}
          columns={columns}
          selectedRowsState={[selectedEvent, setSelectedEvents]}
          responseData={useResponse}
          viewRecordAbility={{ title: 'view animal_event' }}
          filteredTotalsParams={{
            recordName: 'records',
          }}
          notShowSelectedChecked={'has_done'}
          disableAllPagination={true}
        />
      </TableVerticalSizeFixer>
    </div>
  );
}
